import { useState } from 'react';
import axios from 'axios';
import { getUserImageUploadUrl } from '../redux/Users/actions';

// Uploads a profile photo for a user via a presigned S3 POST. Returns the
// presign `imageUrl` (pass it to the commit step to finalize) and
// `requiresLiveCapture` (true for drivers, whose photo is face-matched on
// commit). The backend returns { uploadUrl, fields, imageUrl, requiresLiveCapture };
// S3 requires the file field appended last.
//
// The presign policy carries a `["starts-with", "$Content-Type", "image/"]`
// condition but the backend does NOT include a Content-Type entry in `fields`,
// so the client must supply it — otherwise S3 rejects the POST with 403
// AccessDenied and the upload silently fails. Append it (from the file's own
// MIME type) before the `file` field, and only when the backend didn't provide
// one, so a future backend change that adds it wins.
const useUploadUserImage = (userId) => {
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    setUploading(true);
    try {
      const { success, data } = await getUserImageUploadUrl(userId)();
      if (!success || !data?.uploadUrl) {
        return { imageUrl: null };
      }
      const { uploadUrl, fields, imageUrl, requiresLiveCapture } = data;
      const form = new FormData();
      Object.entries(fields || {}).forEach(([key, value]) => form.append(key, value));
      if (!fields || !('Content-Type' in fields)) {
        form.append('Content-Type', file.type || 'image/jpeg');
      }
      form.append('file', file);
      await axios.post(uploadUrl, form);
      return { imageUrl, requiresLiveCapture };
    } catch (error) {
      return { imageUrl: null, error };
    } finally {
      setUploading(false);
    }
  };

  return { upload, isUploading: uploading };
};

export default useUploadUserImage;
